import { convertChannelCasing } from 'lib-shared-api-utils';
import type { Message } from 'lib-shared-legacy-types';
import type {
  ConversationChatbotMessageAddedEvent,
  ConversationEmailMessageAddedEvent,
  ConversationFacebookMessengerMessageAddedEvent,
  ConversationMessengerMessageAddedEvent,
  ConversationTwitterMessageAddedEvent,
  ConversationWhatsAppMessageAddedEvent,
  ConversationWidgetChatMessageAddedEvent,
} from 'lib-shared-types-api';
import { ComponentProps } from 'react';

import { useColorMode } from 'app/utils/colorMode';
import { getMessageMarkup } from 'app/utils/messages';
import { HTMLShadowDomRenderer } from 'components/HTMLShadowDomRenderer';
import { ConversationEventGroupConfigBase } from 'components/conversation/conversationEventGroups/conversationEventGroups.types';

import { ConversationTextualMessage } from '../../textualMessage/conversationTextualMessage';
import { ConversationTextualMessagePropBase } from '../../textualMessage/conversationTextualMessage.types';

/** Represents the conversation email event group configuration. */
export type ConversationHTMLEventGroupConfig = ConversationEventGroupConfigBase<
  | 'group:contact_form'
  | 'group:email'
  | 'group:messenger'
  | 'group:adachat'
  | 'group:digitalgeniuschat'
  | 'group:kindlychat'
  | 'group:trustpilotreviews'
  | 'group:facebook_messenger'
  | 'group:whats_app'
  | 'group:widget_chat'
  | 'group:messenger'
  | 'group:sms'
  | 'group:twitter',
  Pick<ConversationHTMLEventGroupProps, 'hideAttachments' | 'hideGutterActions'>
>;

type ConversationHTMLEventGroupProps = ConversationTextualMessagePropBase<
  | ConversationEmailMessageAddedEvent
  | ConversationMessengerMessageAddedEvent
  | ConversationChatbotMessageAddedEvent
  | ConversationFacebookMessengerMessageAddedEvent
  | ConversationMessengerMessageAddedEvent
  | ConversationWhatsAppMessageAddedEvent
  | ConversationWidgetChatMessageAddedEvent
  | ConversationTwitterMessageAddedEvent,
  Pick<
    ComponentProps<typeof ConversationTextualMessage.Events>,
    'hideAttachments'
  >
>;

/** Represents the component for displaying a conversation email event group. */
export const ConversationHTMLEventGroup = ({
  author,
  className,
  events,
  hideAttachments,
  hideGutterActions,
}: ConversationHTMLEventGroupProps): JSX.Element => {
  const { colorMode } = useColorMode();

  const timestamp = events[events.length - 1].created_at;

  return (
    <ConversationTextualMessage
      className={className}
      author={author}
      timestamp={timestamp}
      hideGutterActions={hideGutterActions}
      variant={
        events[0].direction === 'outbound'
          ? 'textualOutbound'
          : 'textualInbound'
      }
    >
      <ConversationTextualMessage.Events
        events={events}
        hideAttachments={hideAttachments}
        renderEventText={(event) => {
          const { channel, ...rest } = event;
          const truncatedChannel = convertChannelCasing(channel, 'truncate');

          return (
            event.text && (
              <HTMLShadowDomRenderer
                html={getMessageMarkup({
                  channel: truncatedChannel,
                  ...rest,
                } as Message)}
                colorMode={colorMode}
              />
            )
          );
        }}
      />
    </ConversationTextualMessage>
  );
};
